import { FunctionComponent, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import parse from 'html-react-parser';
import Status, { StatusValue } from '../Data/Status';
import Logo from '../Images/icon64.png';

interface ErrorPageProps {
  errorDetails?: ReactNode;
  status: StatusValue;
  setStatus: (status: StatusValue) => void;
}

export const ErrorPage: FunctionComponent<ErrorPageProps> = ({
  errorDetails,
  status,
  setStatus
}) => {
  let errorText = 'Something went wrong ☹';
  let message: ReactNode = parse(`This error will only show if we don't know what went wrong.
  If you keep getting it, contact our support at  <a href='mailto:support@npstoday.com'>support@npstoday.com</a>`);
  if (status === Status.RESPONSE_LIMIT_REACHED) {
    errorText = 'This survey is no longer accepting responses';
    message =
      "Thank you for taking the time to respond. Unfortunately your answer couldn't be saved.";
  } else if (errorDetails) {
    message = errorDetails;
  } else if (status === Status.MISSINGGUID) {
    errorText = 'Your link is broken ☹';
    message = `You most likely see this error because the link you clicked on
    is missing something or you have received the link by mistake.`;
  } else if (status === Status.EXPIREDTOKEN) {
    errorText = 'Your link has expired';
    message = 'You see this error because the link you were sent has expired.';
  } else if (status === Status.NO_INTERNET) {
    errorText = "It seems like you don't have any connection to the internet";
    message =
      "Please don't refresh your browser, as your progress will be lost. When a connection is established you can continue from where you left off.";
  }

  if (status === Status.TURNSTILE) {
    errorText = 'Human verification failed';
  }

  if (status === Status.SURVEYEXPIRED) {
    errorText = 'Survey expired';
  }

  const [stylingReady, setStylingReady] = useState(false);

  useEffect(() => {
    async function fixFlickering() {
      try {
        await import('../Dependencies/bootstrap-5.2.0.min.css');
      } finally {
        setStylingReady(true); // prevents flickering at start of survey
      }
    }
    fixFlickering();
  }, []);

  useEffect(() => {
    function handleOnline() {
      // if the user sees the error page due to no internet connection, set status to SUCCESS upon connection, so survey can be completed
      if (status === Status.NO_INTERNET) {
        setStatus(Status.SUCCESS);
      }
    }

    globalThis.addEventListener('online', handleOnline);

    return () => {
      globalThis.removeEventListener('online', handleOnline);
    };
  }, [status, setStatus]);

  return (
    <>
      {stylingReady && (
        <Container>
          <br />
          <Row>
            <Col md={6} className="mx-auto text-center">
              <div className="header-title" style={{ margin: '2rem 0px' }}>
                <img src={Logo} alt="nps.today logo" />
                <h1
                  className="wv-heading--title"
                  style={{
                    fontSize: '31px',
                    lineHeight: '40px',
                    fontWeight: 600,
                    color: '#4c5357'
                  }}
                >
                  nps.today
                </h1>
                <h2
                  className="wv-heading--subtitle"
                  style={{
                    color: '#5e8396',
                    fontSize: '21px',
                    fontWeight: 400,
                    lineHeight: '32px'
                  }}
                >
                  <br />
                  <span style={{ fontSize: '21px' }}>{errorText}</span>
                </h2>
              </div>
            </Col>
          </Row>

          <br />
          <Row>
            <Col md={4} className="mx-auto text-center">
              <p style={{ fontSize: '16px' }} id="eDetails">
                {message}
              </p>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};
